import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Tabs,
  Tab,
  Accordion,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import { 
  FaArrowLeft, 
  FaHome, 
  FaPaintBrush, 
  FaDoorClosed, 
  FaWindowRestore, 
  FaWater, 
  FaBath, 
  FaBrush, 
  FaHammer, 
  FaSync, 
  FaCheckCircle, 
  FaClock, 
  FaUser, 
  FaEnvelope, 
  FaCalendar, 
  FaCheck, 
  FaTimes, 
  FaChevronDown, 
  FaChevronUp, 
  FaBuilding, 
  FaTools, 
  FaWrench,
  FaMugHot,
  FaGem,
  FaImage,
  FaBox,
  FaCog,
  FaToilet,
  FaFire,
  FaSink,
  FaWind,
  FaTshirt
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const Selections = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [selectionData, setSelectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('exterior');
  const [expandedSections, setExpandedSections] = useState({});
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  const fetchSelections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(
        `${API_BASE}/data/getOwnerSelectionByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setSelectionData(res.data.ownerSelection);
      } else {
        setError('Failed to load selection data');
        toast.error('Failed to load selection data');
      }
    } catch (error) {
      console.error('Fetch selections error:', error);
      setError('Error loading selection data');
      toast.error('Error loading selection data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchSelections();
    }
  }, [projectId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    const months = Math.floor(diffInSeconds / 2592000);
    if (months < 1) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    
    const years = Math.floor(months / 12);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  const renderBooleanIcon = (value) => {
    return value ? (
      <Badge bg="success" className="d-inline-flex align-items-center">
        <FaCheck size={12} className="me-1" /> Yes
      </Badge>
    ) : (
      <Badge bg="secondary" className="d-inline-flex align-items-center">
        <FaTimes size={12} className="me-1" /> No
      </Badge>
    );
  };

  const renderSectionAccordion = (title, icon, data, color = 'primary', hasRooms = false, roomKey = 'rooms') => {
    if (!data || (typeof data !== 'object')) return null;
    
    const entries = Object.entries(data).filter(([key]) => 
      key !== '_id' && key !== '__v' && key !== roomKey
    );
    
    const hasContent = entries.length > 0 || (hasRooms && data[roomKey] && data[roomKey].length > 0);
    if (!hasContent) return null;

    return (
      <Accordion.Item eventKey={title}>
        <Accordion.Header>
          <div className="d-flex align-items-center w-100">
            <div className={`bg-${color} text-white rounded-circle p-2 me-3`}>
              {icon}
            </div>
            <h5 className="mb-0">{title}</h5>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          {/* Main Data */}
          {entries.length > 0 && (
            <Row className="g-3 mb-4">
              {entries.map(([key, value]) => {
                if (!value && value !== false && value !== 0) return null;
                
                return (
                  <Col md={6} lg={4} key={key}>
                    <div className="p-3 bg-light rounded">
                      <div className="small text-muted text-uppercase mb-1">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="fw-medium">
                        {typeof value === 'boolean' 
                          ? renderBooleanIcon(value)
                          : Array.isArray(value)
                          ? (
                            <div className="mt-2">
                              {value.map((item, idx) => (
                                <Badge key={idx} bg="light" text="dark" className="me-1 mb-1">
                                  {typeof item === 'object' ? JSON.stringify(item) : item}
                                </Badge>
                              ))}
                            </div>
                          )
                          : typeof value === 'object'
                          ? (
                            <div className="mt-2">
                              {Object.entries(value).map(([k, v]) => (
                                <div key={k} className="mb-1">
                                  <small className="text-muted">{k}:</small>
                                  <div>{typeof v === 'boolean' ? renderBooleanIcon(v) : v}</div>
                                </div>
                              ))}
                            </div>
                          )
                          : value
                        }
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
          
          {/* Rooms Data */}
          {hasRooms && data[roomKey] && data[roomKey].length > 0 && (
            <div className="mt-3">
              <h6 className="mb-3">
                {title === 'Marble' ? 'Bathrooms' : 
                 title === 'Plumbing' ? 'Baths' : 
                 title === 'Paint Colors' ? 'Room Paint Colors' :
                 title === 'Flooring' ? 'Room Flooring' :
                 title === 'Hardware' ? 'Bathroom Hardware' : 'Rooms'}
              </h6>
              {data[roomKey].map((room, idx) => (
                <Card key={idx} className="mb-3 border">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">{room.roomName || room.bathroom || room.room || room.bath || `Item ${idx + 1}`}</h6>
                  </Card.Header>
                  <Card.Body>
                    {room.values ? (
                      <Row>
                        {room.values.map((item, itemIdx) => (
                          <Col md={6} key={itemIdx} className="mb-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{item.key}:</span>
                              <span className="fw-medium">{item.value}</span>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <Row>
                        {Object.entries(room).map(([key, value]) => {
                          if (key === '_id' || key === 'roomName' || key === 'bathroom' || key === 'room' || key === 'bath') return null;
                          return (
                            <Col md={6} key={key} className="mb-2">
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}:
                                </span>
                                <span className="fw-medium">
                                  {typeof value === 'boolean' ? renderBooleanIcon(value) : value}
                                </span>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  const renderArrayAccordion = (title, icon, data, color = 'primary', itemName = 'items') => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    return (
      <Accordion.Item eventKey={title}>
        <Accordion.Header>
          <div className="d-flex align-items-center w-100">
            <div className={`bg-${color} text-white rounded-circle p-2 me-3`}>
              {icon}
            </div>
            <h5 className="mb-0">{title}</h5>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <Row>
            {data.map((item, index) => (
              <Col md={6} key={index} className="mb-3">
                <Card className="h-100 border">
                  <Card.Body>
                    {Object.entries(item).map(([key, value]) => {
                      if (key === '_id') return null;
                      return (
                        <div key={key} className="mb-2">
                          <small className="text-muted d-block">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </small>
                          <div className="fw-medium">
                            {typeof value === 'boolean' 
                              ? renderBooleanIcon(value)
                              : Array.isArray(value)
                              ? value.join(', ')
                              : typeof value === 'object'
                              ? JSON.stringify(value)
                              : value}
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  const renderSimpleAccordion = (title, icon, data, color = 'primary') => {
    if (!data || (typeof data !== 'object')) return null;
    
    const entries = Object.entries(data).filter(([key]) => key !== '_id' && key !== '__v');
    if (entries.length === 0) return null;

    return (
      <Accordion.Item eventKey={title}>
        <Accordion.Header>
          <div className="d-flex align-items-center w-100">
            <div className={`bg-${color} text-white rounded-circle p-2 me-3`}>
              {icon}
            </div>
            <h5 className="mb-0">{title}</h5>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <Row className="g-3">
            {entries.map(([key, value]) => {
              if (!value && value !== false && value !== 0) return null;
              
              return (
                <Col md={6} lg={4} key={key}>
                  <div className="p-3 bg-light rounded">
                    <div className="small text-muted text-uppercase mb-1">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                    <div className="fw-medium">
                      {typeof value === 'boolean' 
                        ? renderBooleanIcon(value)
                        : Array.isArray(value)
                        ? (
                          <div className="mt-2">
                            {value.map((item, idx) => (
                              <Badge key={idx} bg="light" text="dark" className="me-1 mb-1">
                                {typeof item === 'object' ? JSON.stringify(item) : item}
                              </Badge>
                            ))}
                          </div>
                        )
                        : typeof value === 'object'
                        ? (
                          <div className="mt-2">
                            {Object.entries(value).map(([k, v]) => (
                              <div key={k} className="mb-1">
                                <small className="text-muted">{k}:</small>
                                <div>{typeof v === 'boolean' ? renderBooleanIcon(v) : v}</div>
                              </div>
                            ))}
                          </div>
                        )
                        : value
                      }
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading owner selections...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Error Loading Selections</h4>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchSelections}>
            <FaSync className="me-2" />
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!selectionData) {
    return (
      <Container className="py-5">
        <Alert variant="info">
          <h4>No Selection Data Found</h4>
          <p>No owner selections have been recorded for this project yet.</p>
          <Button variant="outline-primary" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" />
            Back to Projects
          </Button>
        </Alert>
      </Container>
    );
  }

  const { exterior, interior, createdBy, createdAt, updatedAt } = selectionData;

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back
              </Button>
              <h2 className="fw-bold mb-2">
                <FaHome className="me-3" />
                Owner Selections
              </h2>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Badge bg="primary" className="px-3 py-2">
                  Project ID: {projectId?.substring(0, 8)}...
                </Badge>
                <Badge bg="success" className="px-3 py-2">
                  <FaClock className="me-2" />
                  Updated {getTimeAgo(updatedAt)}
                </Badge>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={fetchSelections}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* META INFO */}
          <Card className="mb-4 border-0 shadow-sm bg-light">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                      <FaUser className="text-primary" size={24} />
                    </div>
                    <div>
                      <div className="small text-muted">Created By</div>
                      <div className="fw-medium">{createdBy?.name}</div>
                      <div className="small text-muted d-flex align-items-center">
                        <FaEnvelope size={12} className="me-1" />
                        {createdBy?.email}
                      </div>
                    </div>
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                      <FaCalendar className="text-success" size={24} />
                    </div>
                    <div>
                      <div className="small text-muted">Created</div>
                      <div className="fw-medium">{formatDate(createdAt)}</div>
                    </div>
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                      <FaClock className="text-warning" size={24} />
                    </div>
                    <div>
                      <div className="small text-muted">Last Updated</div>
                      <div className="fw-medium">{formatDate(updatedAt)}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* MAIN TABS */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
            fill
          >
            <Tab 
              eventKey="exterior" 
              title={
                <div className="d-flex align-items-center">
                  <FaHome className="me-2" />
                  Exterior Selections
                </div>
              }
            >
              <div className="mt-4">
                <Accordion defaultActiveKey={[]} alwaysOpen>
                  {/* EXTERIOR SECTIONS */}
                  {renderSimpleAccordion('Brick', <FaHammer size={20} />, exterior?.brick, 'warning')}
                  {renderSimpleAccordion('Paint', <FaPaintBrush size={20} />, exterior?.paint, 'info')}
                  {renderSimpleAccordion('Front Door', <FaDoorClosed size={20} />, exterior?.frontDoor, 'secondary')}
                  {renderSimpleAccordion('Garage Door', <FaDoorClosed size={20} />, exterior?.garageDoor, 'dark')}
                  {renderSimpleAccordion('Windows', <FaWindowRestore size={20} />, exterior?.windows, 'primary')}
                  {renderSimpleAccordion('Roof', <FaBuilding size={20} />, exterior?.roof, 'danger')}
                  {renderSimpleAccordion('Fence', <FaTools size={20} />, exterior?.fence, 'success')}
                  {renderSimpleAccordion('Gutters', <FaWater size={20} />, exterior?.gutters, 'info')}
                  {renderSimpleAccordion('Shutters', <FaWindowRestore size={20} />, exterior?.shutters, 'warning')}
                  
                  {/* Rear Doors List */}
                  {exterior?.rearDoors && (
                    <Accordion.Item eventKey="Rear Doors">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-warning text-white rounded-circle p-2 me-3">
                            <FaDoorClosed size={20} />
                          </div>
                          <h5 className="mb-0">Rear Doors</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          {exterior.rearDoors.map((door, idx) => (
                            <Col md={6} key={door._id || idx} className="mb-3">
                              <Card className="h-100 border">
                                <Card.Body>
                                  <h6 className="text-primary">{door.location}</h6>
                                  <div className="small">
                                    <div className="mb-1">
                                      <span className="text-muted">Supplier:</span>
                                      <span className="ms-2 fw-medium">{door.supplier}</span>
                                    </div>
                                    <div className="mb-1">
                                      <span className="text-muted">Make/Model:</span>
                                      <span className="ms-2 fw-medium">{door.make} {door.model}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted">Color:</span>
                                      <Badge bg="light" text="dark" className="ms-2">
                                        {door.color}
                                      </Badge>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {/* Other Exterior Items */}
                  {exterior?.retainingWall && (
                    <Accordion.Item eventKey="Retaining Wall">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-info text-white rounded-circle p-2 me-3">
                            <FaBuilding size={20} />
                          </div>
                          <h5 className="mb-0">Retaining Wall</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <p className="mb-0">{exterior.retainingWall}</p>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {exterior?.other && (
                    <Accordion.Item eventKey="Other Exterior Notes">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-secondary text-white rounded-circle p-2 me-3">
                            <FaTools size={20} />
                          </div>
                          <h5 className="mb-0">Other Notes</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <p className="mb-0">{exterior.other}</p>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                </Accordion>
              </div>
            </Tab>
            
            <Tab 
              eventKey="interior" 
              title={
                <div className="d-flex align-items-center">
                  <FaBath className="me-2" />
                  Interior Selections
                </div>
              }
            >
              <div className="mt-4">
                <Accordion defaultActiveKey={[]} alwaysOpen>
                  {/* PAINT COLORS */}
                  {renderSectionAccordion('Paint Colors', <FaBrush size={20} />, interior?.paintColors, 'info', true, 'rooms')}
                  
                  {/* FLOORING */}
                  {renderSectionAccordion('Flooring', <FaHammer size={20} />, interior?.flooring, 'warning', true, 'rooms')}
                  
                  {/* INTERIOR TRIM */}
                  {interior?.interiorTrim && (
                    <Accordion.Item eventKey="Interior Trim">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-primary text-white rounded-circle p-2 me-3">
                            <FaBrush size={20} />
                          </div>
                          <h5 className="mb-0">Interior Trim</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          {Object.entries(interior.interiorTrim).map(([key, value]) => {
                            if (key === '_id' || key === '__v') return null;
                            
                            if (key === 'otherItems') {
                              if (Array.isArray(value) && value.length > 0) {
                                return (
                                  <Col md={12} key={key} className="mb-3">
                                    <div className="p-3 bg-light rounded">
                                      <div className="small text-muted text-uppercase mb-1">Other Items</div>
                                      {value.map((item, idx) => (
                                        <div key={idx} className="mb-2">
                                          <Badge bg="light" text="dark" className="me-1">
                                            {item.room}: {item.item}
                                          </Badge>
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                );
                              }
                              return null;
                            }
                            
                            return (
                              <Col md={6} lg={4} key={key} className="mb-3">
                                <div className="p-3 bg-light rounded h-100">
                                  <div className="small text-muted text-uppercase mb-1">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                  </div>
                                  <div className="fw-medium">{value}</div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {/* HARDWARE */}
                  {renderSectionAccordion('Hardware', <FaWrench size={20} />, interior?.hardware, 'dark', true, 'bathrooms')}
                  
                  {/* FIREPLACE */}
                  {renderSimpleAccordion('Fireplace', <FaFire size={20} />, interior?.fireplace, 'danger')}
                  
                  {/* KITCHEN COUNTERS */}
                  {renderSimpleAccordion('Kitchen Counters', <FaMugHot size={20} />, interior?.kitchenCounters, 'success')}
                  
                  {/* APPLIANCES */}
                  {renderSimpleAccordion('Appliances', <FaCog size={20} />, interior?.appliances, 'warning')}
                  
                  {/* MARBLE */}
                  {renderSectionAccordion('Marble', <FaGem size={20} />, interior?.marble, 'info', true, 'bathrooms')}
                  
                  {/* PLUMBING */}
                  {renderSectionAccordion('Plumbing', <FaWater size={20} />, interior?.plumbing, 'primary', true, 'baths')}
                  
                  {/* SHOWER GLASS */}
                  {renderSimpleAccordion('Shower Glass', <FaWindowRestore size={20} />, interior?.showerGlass, 'secondary')}
                  
                  {/* MIRRORS */}
                  {renderSimpleAccordion('Mirrors', <FaImage size={20} />, interior?.mirrors, 'dark')}
                  
                  {/* WALLPAPER */}
                  {renderArrayAccordion('Wallpaper', <FaBrush size={20} />, interior?.wallpaper, 'info', 'wallpaper')}
                  
                  {/* CABINET STYLE */}
                  {interior?.cabinetStyle && (
                    <Accordion.Item eventKey="Cabinet Style">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-success text-white rounded-circle p-2 me-3">
                            <FaBox size={20} />
                          </div>
                          <h5 className="mb-0">Cabinet Style</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="p-3 bg-light rounded">
                          <div className="fw-medium">{interior.cabinetStyle}</div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {/* GARAGE DOOR OPENER */}
                  {interior?.garageDoorOpener && (
                    <Accordion.Item eventKey="Garage Door Opener">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-secondary text-white rounded-circle p-2 me-3">
                            <FaDoorClosed size={20} />
                          </div>
                          <h5 className="mb-0">Garage Door Opener</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="p-3 bg-light rounded">
                          <div className="fw-medium">
                            {Array.isArray(interior.garageDoorOpener.doors) 
                              ? interior.garageDoorOpener.doors.join(', ')
                              : interior.garageDoorOpener.doors}
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {/* OTHER INTERIOR NOTES */}
                  {interior?.other && (
                    <Accordion.Item eventKey="Other Interior Notes">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <div className="bg-secondary text-white rounded-circle p-2 me-3">
                            <FaCog size={20} />
                          </div>
                          <h5 className="mb-0">Other Notes</h5>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <p className="mb-0">{interior.other}</p>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                </Accordion>
              </div>
            </Tab>
            
            <Tab 
              eventKey="summary" 
              title={
                <div className="d-flex align-items-center">
                  <FaCheckCircle className="me-2" />
                  Summary
                </div>
              }
            >
              <div className="mt-4">
                <Row>
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-primary text-white border-0">
                        <h5 className="mb-0">Exterior Summary</h5>
                      </Card.Header>
                      <Card.Body>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Brick Company</span>
                            <Badge bg="primary">
                              {exterior?.brick?.brickCompany || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Paint Brand</span>
                            <Badge bg="info">
                              {exterior?.paint?.brand || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Front Door Supplier</span>
                            <Badge bg="secondary">
                              {exterior?.frontDoor?.supplier || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Windows Supplier</span>
                            <Badge bg="primary">
                              {exterior?.windows?.supplier || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Roof Style</span>
                            <Badge bg="danger">
                              {exterior?.roof?.style || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-success text-white border-0">
                        <h5 className="mb-0">Interior Summary</h5>
                      </Card.Header>
                      <Card.Body>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Paint Brand</span>
                            <Badge bg="info">
                              {interior?.paintColors?.brand || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Flooring Company</span>
                            <Badge bg="warning">
                              {interior?.flooring?.flooringCompany || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Kitchen Countertops</span>
                            <Badge bg="success">
                              {interior?.kitchenCounters?.tops || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Appliance Brand (Refrigerator)</span>
                            <Badge bg="warning">
                              {interior?.appliances?.refrigerator || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span>Cabinet Style</span>
                            <Badge bg="dark">
                              {interior?.cabinetStyle || 'Not selected'}
                            </Badge>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                {/* STATS */}
                <Row>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center mb-3">
                      <Card.Body>
                        <div className="display-6 text-primary mb-2">
                          {exterior ? Object.keys(exterior).length : 0}
                        </div>
                        <div className="small text-muted">Exterior Categories</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center mb-3">
                      <Card.Body>
                        <div className="display-6 text-success mb-2">
                          {interior ? Object.keys(interior).length : 0}
                        </div>
                        <div className="small text-muted">Interior Categories</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center mb-3">
                      <Card.Body>
                        <div className="display-6 text-warning mb-2">
                          {getTimeAgo(updatedAt).split(' ')[0]}
                        </div>
                        <div className="small text-muted">Since Last Update</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Tab>
          </Tabs>

          
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Selections;